import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function QueueDisplay() {
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const [nowServing, setNowServing] = useState([]);
  const [preparing, setPreparing] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [servedTimers, setServedTimers] = useState({});
  const [stallType, setStallType] = useState(null);

  // Function to move order from preparing to served (without announcement)
  const moveToServed = (order) => {
    if (!order || nowServing.some(served => served.queueNumber === order.queueNumber)) {
      return;
    }
    
    // Add to the end of the array to maintain original order
    setNowServing(prev => [...prev, { ...order, servedAt: Date.now() }]);
    
    // Remove from preparing
    setPreparing(prev => prev.filter(item => item.id !== order.id));
    
    // Set timer to remove from served and delete from Firestore after 5 minutes
    const servedTimer = setTimeout(async () => {
      await deleteOrderFromFirestore(order);
      setNowServing(prev => prev.filter(item => item.id !== order.id));
      setServedTimers(prev => {
        const newTimers = {...prev};
        delete newTimers[order.id];
        return newTimers;
      });
    }, 5 * 60 * 1000); // 5 minutes
    
    setServedTimers(prev => ({
      ...prev,
      [order.id]: servedTimer
    }));
  };

  // Function to manually dismiss a served order
  const dismissServedOrder = (e, orderId, timerId) => {
    e.stopPropagation(); // Prevent triggering the click event on parent
    clearTimeout(timerId);
    setNowServing(prev => prev.filter(item => item.id !== orderId));
    setServedTimers(prev => {
      const newTimers = {...prev};
      delete newTimers[orderId];
      return newTimers;
    });
  };

  // Function to speak text
  const speak = (text) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Speech synthesis not supported in this browser');
      return;
    }

    // Cancel any current speech
    window.speechSynthesis.cancel();

    const speech = new SpeechSynthesisUtterance();
    speech.text = text;
    speech.volume = 1;
    speech.rate = 0.9;
    speech.pitch = 1;

    // Get voices and wait for them to load if needed
    let voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        voices = window.speechSynthesis.getVoices();
        setVoiceAndSpeak(speech, voices);
      };
    } else {
      setVoiceAndSpeak(speech, voices);
    }
  };

  const setVoiceAndSpeak = (speech, voices) => {
    const preferredVoices = [
      'Google UK English Female', 
      'Google UK English Male', 
      'Samantha', 
      'Alex',
      'Google UK English',
      'Microsoft Zira - English (United States)'
    ];
    
    // Try to find a preferred voice, or use the first available one
    const voice = voices.find(v => preferredVoices.some(pv => v.name.includes(pv))) || voices[0];
    
    if (voice) {
      speech.voice = voice;
    }
    
    try {
      window.speechSynthesis.speak(speech);
    } catch (error) {
      console.error('Error with speech synthesis:', error);
    }
  };

  // Function to delete an order from Firestore
  // Function to call and dismiss a queue number
  const callAndDismissNumber = async (order) => {
    // Only announce when clicked, not when first served
    speak(`Queue number ${order.queueNumber} is now ready.`);
    // Simply remove from UI; preorder backend removed
    setNowServing(prev => prev.filter(item => item.id !== order.id));
  };

  // Function to announce when a new order is ready to be served
  const announceNewServing = (newServing) => {
    if (newServing.length > 0) {
      const latestOrder = newServing[0];
      
      // Only announce if not already in served
      if (!nowServing.some(order => order.queueNumber === latestOrder.queueNumber)) {
        moveToServed(latestOrder);
      }
    }
  };

  // Handle order completion events from Kitchen
  useEffect(() => {
    const handleOrderCompleted = (event) => {
      const { queueNumber, customerName, orderId } = event.detail;
      
      // Update the queue first
      setPreparing(prevPreparing => {
        const orderToMove = prevPreparing.find(order => order.queueNumber === queueNumber);
        if (orderToMove) {
          setNowServing(prevServing => [
            { ...orderToMove, id: orderId || Date.now().toString() },
            ...prevServing
          ].slice(0, 10));
          
          return prevPreparing.filter(order => order.queueNumber !== queueNumber);
        }
        return prevPreparing;
      });
    };
    
    window.addEventListener('orderCompleted', handleOrderCompleted);
    
    return () => {
      window.removeEventListener('orderCompleted', handleOrderCompleted);
    };
  }, []);

  // Handle order announcement events from Kitchen
  useEffect(() => {
    const handleOrderAnnouncement = (event) => {
      const { queueNumber, customerName } = event.detail;
      const message = `Queue number ${queueNumber} is now ready for ${customerName}`;
      
      // Speak the announcement
      speak(message);
      
      // Update the UI to show the order as served
      setPreparing(prevPreparing => {
        const orderToMove = prevPreparing.find(order => order.queueNumber === queueNumber);
        if (orderToMove) {
          setNowServing(prevServing => [
            { ...orderToMove, servedAt: new Date() },
            ...prevServing
          ].slice(0, 10));
          return prevPreparing.filter(order => order.queueNumber !== queueNumber);
        }
        return prevPreparing;
      });
    };
    
    window.addEventListener('orderAnnouncement', handleOrderAnnouncement);
    
    return () => {
      window.removeEventListener('orderAnnouncement', handleOrderAnnouncement);
    };
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      Object.values(servedTimers).forEach(timer => clearTimeout(timer));
    };
  }, [servedTimers]);

  useEffect(() => {
    if (user) {
      loadCompanyName();
      loadCompanyType();
      // Preorder/queue collection removed; keep lists empty
      setNowServing([]);
      setPreparing([]);
      return () => {};
    }
  }, [user]);

  const loadCompanyName = async () => {
    const ref = doc(db, `companies/${companyId}/companyProfile/info`);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setCompanyName(snap.data().companyName || '');
    }
  };

  const loadCompanyType = async () => {
    const ref = doc(db, `companies/${companyId}/companySettings/info`);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setStallType(snap.data().stallType || '');
    }
  };

  const today = new Date().toLocaleDateString('en-MY', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen text-white">
      <div className=" mx-auto">
        {/* Top Bar */}
        <div className="flex items-center justify-center p-4 bg-gray-800/80 backdrop-blur-sm border-b border-gray-700/50">
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {companyName || 'Queue Display'}
            </h1>
            <span className="text-sm text-gray-300">{today}</span>
          </div>
        </div>

        {/* Queue Section */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className=" mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Preparing */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></span>
                  Preparing
                  <span className="ml-2 text-sm px-3 py-1 bg-yellow-900/30 text-yellow-300 rounded-full">
                    {preparing.length} in queue
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {preparing.map((order) => (
                  <div 
                    key={order.id} 
                    className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border border-yellow-700/30 rounded-xl p-5 text-center transform transition-all duration-300 hover:scale-105 hover:border-yellow-500/50"
                  >
                    <div className="text-4xl font-black text-yellow-400 mb-2">
                      {String(order.queueNumber).padStart(3, '0')}
                    </div>
                    <div className="text-sm text-yellow-100 font-medium truncate">
                      {order.customerName || 'Customer'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Now Serving */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                {stallType === "fnb" ? (
                <h2 className="text-2xl font-bold text-green-400 flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                  Served
                  <span className="ml-2 text-sm px-3 py-1 bg-green-900/30 text-green-300 rounded-full">
                    {nowServing.length} being served
                  </span>
                </h2>
                ) : (
                <h2 className="text-2xl font-bold text-green-400 flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                  Ready
                  <span className="ml-2 text-sm px-3 py-1 bg-green-900/30 text-green-300 rounded-full">
                    {nowServing.length} is ready
                  </span>
                </h2>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {nowServing.map((order) => (
                  <div 
                    key={order.id} 
                    className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/30 rounded-xl p-5 text-center transform transition-all duration-300 hover:scale-105 hover:border-green-500/50 cursor-pointer"
                    onClick={() => callAndDismissNumber(order)}
                  >
                    <div className="text-4xl font-black text-green-400 mb-2">
                      {String(order.queueNumber).padStart(3, '0')}
                    </div>
                    <div className="text-sm text-green-100 font-medium truncate">
                      {order.customerName || 'Customer'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
